/**
 * Индекс адреса регистрации — при открытии дела (сверка «Айнбиндер»,
 * 26.10.2026). С 25.10 индекс подбирается после ввода дома, а в делах,
 * заполненных раньше, его нет. Решение заказчика: подбирать самим при
 * открытии, если есть регион, город или населённый пункт, улица и дом.
 * Один раз на дело; без DaData или при сбое — молча, как было.
 */

import { useEffect, useRef, useState } from 'react';
import { updateAddressPart, type Address, type CaseProfile } from '@bd/domain';
import { api } from '../../api.js';
import { isPoBox, postalParts } from '../../components/fields/AddressField.js';

const complete = (a: Address | undefined): a is Address => {
  if (!a || a.postalCode?.trim() || isPoBox(a)) return false;
  const parts = postalParts(a);
  return Boolean(parts.region && (parts.city || parts.settlement) && parts.street && parts.house);
};

export function usePostalCodeOnOpen(
  profile: CaseProfile | null,
  update: (apply: (p: CaseProfile) => CaseProfile) => void,
  readOnly: boolean,
): string | null {
  const [message, setMessage] = useState<string | null>(null);
  const done = useRef<string | null>(null);

  useEffect(() => {
    if (!profile || readOnly || done.current === profile.caseId) return;
    done.current = profile.caseId;
    const targets = (['registrationAddress', 'stayAddress'] as const).filter((key) => complete(profile.debtor[key]));
    if (!targets.length) return;

    void (async () => {
      const found: string[] = [];
      for (const key of targets) {
        const parts = postalParts(profile.debtor[key]!);
        try {
          const postalCode = await api.dadata.postalCode(parts);
          if (!postalCode) continue;
          // Пока шёл запрос, адрес могли поправить — тогда индекс не ставится
          update((p) => {
            const now = p.debtor[key];
            if (!now || now.postalCode?.trim() || JSON.stringify(postalParts(now)) !== JSON.stringify(parts)) return p;
            return { ...p, debtor: { ...p.debtor, [key]: updateAddressPart(now, 'postalCode', postalCode) } };
          });
          found.push(`${key === 'registrationAddress' ? 'адреса регистрации' : 'адреса пребывания'} — ${postalCode}`);
        } catch {
          // DaData недоступна — индекс вписывается вручную
        }
      }
      if (found.length) setMessage(`Подобран индекс ${found.join(', ')}. Проверьте в разделе «Должник».`);
    })();
  }, [profile, update, readOnly]);

  return message;
}
